// §31 — Toast notification system. Replaces the inline <div className="err">
// and <div className="note"> banners that used to live inside each page.
//
// Usage:
//   const toast = useToast();
//   toast.success("Saved.");
//   toast.error("Couldn't save.");
//   toast.info("Heads-up.");
//
// All toasts auto-dismiss after 4s; user can also click the × to close early.
// The ToastProvider lives near the root (in App.jsx) so any view can call
// useToast() without prop drilling.

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

const ToastCtx = createContext(null);

let nextId = 1;
const ICONS = { success: CheckCircle2, error: AlertCircle, info: Info };

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback((kind, message, opts = {}) => {
    if (!message) return;
    const id = nextId++;
    const ttl = opts.duration ?? 4000;
    setToasts(prev => [...prev, { id, kind, message }]);
    if (ttl > 0) setTimeout(() => dismiss(id), ttl);
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    success: (m, o) => push("success", m, o),
    error:   (m, o) => push("error",   m, { duration: 6000, ...o }),
    info:    (m, o) => push("info",    m, o),
    dismiss
  }), [push, dismiss]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toastTray" role="status" aria-live="polite">
        {toasts.map(t => {
          const Icon = ICONS[t.kind] || Info;
          return (
            <div key={t.id} className={`toast toast--${t.kind}`}>
              <Icon size={18} />
              <span>{t.message}</span>
              <button
                type="button"
                className="toast-close"
                aria-label="Dismiss"
                onClick={() => dismiss(t.id)}
              ><X size={14}/></button>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
